import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, Mail } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (value: number) => {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

interface InvestorDealReviewProps {
  dealTitle: string;
  propertyAddress: string;
  currentBalance: number;
  monthlyPayment: number;
  paymentsRemaining: number;
  balloonAmount: number;
  balloonDueDate: string;
  propertyValue: number;
  investorPrice: number;
  targetYield: number;
  itv: number;
}

export const InvestorDealReview = ({
  dealTitle,
  propertyAddress,
  currentBalance,
  monthlyPayment,
  paymentsRemaining,
  balloonAmount,
  balloonDueDate,
  propertyValue,
  investorPrice,
  targetYield,
  itv,
}: InvestorDealReviewProps) => {
  const handlePrint = () => {
    window.print();
  };

  const generateEmailHTML = () => {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; }
    .header { text-align: center; margin-bottom: 30px; }
    .header h1 { color: #8B4513; font-size: 28px; margin-bottom: 10px; }
    .executive-summary { background: #f8f4f0; border: 2px solid #8B4513; border-radius: 8px; padding: 25px; margin-bottom: 25px; }
    .executive-summary h2 { color: #8B4513; margin-top: 0; }
    .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; text-align: center; }
    .summary-item { padding: 15px; }
    .summary-value { font-size: 32px; font-weight: bold; color: #8B4513; }
    .summary-label { color: #666; font-size: 14px; margin-top: 5px; }
    .card { background: white; border: 1px solid #ddd; border-radius: 8px; padding: 25px; margin-bottom: 25px; }
    .card h3 { color: #8B4513; margin-top: 0; font-size: 20px; }
    .details-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 30px; margin-top: 20px; }
    .details-section h4 { color: #333; font-size: 16px; margin-bottom: 15px; }
    .detail-row { display: flex; justify-content: space-between; margin-bottom: 10px; padding: 8px 0; }
    .detail-label { color: #666; }
    .detail-value { font-weight: bold; }
    .cash-flow-box { background: #f5f5f5; border-radius: 8px; padding: 25px; margin-bottom: 20px; }
    .cash-flow-row { display: flex; justify-content: space-between; margin-bottom: 12px; font-size: 16px; }
    .divider { border-top: 1px solid #ddd; margin: 15px 0; }
    .divider-heavy { border-top: 2px solid #ddd; margin: 15px 0; }
    .positive { color: #16a34a; font-weight: 600; }
    .negative { color: #dc2626; font-weight: 600; }
    .highlight { color: #8B4513; font-weight: bold; font-size: 18px; }
    .metrics-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-top: 20px; }
    .metric-card { background: #f8f4f0; border-radius: 8px; padding: 20px; text-align: center; }
    .metric-value { font-size: 24px; font-weight: bold; color: #8B4513; }
    .metric-label { color: #666; font-size: 13px; margin-top: 5px; }
    .security-item { display: flex; gap: 15px; margin-bottom: 20px; }
    .security-number { width: 32px; height: 32px; background: #f8f4f0; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #8B4513; font-weight: bold; flex-shrink: 0; }
    .security-content h5 { margin: 0 0 5px 0; font-size: 15px; }
    .security-content p { margin: 0; color: #666; }
    .summary-card { background: #f8f4f0; border: 2px solid rgba(139, 69, 19, 0.2); border-radius: 8px; padding: 25px; }
    .summary-card h3 { color: #8B4513; margin-top: 0; }
    .summary-card ul { margin: 10px 0; padding-left: 25px; }
    .summary-card li { margin-bottom: 10px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Investment Opportunity Review</h1>
  </div>

  <div class="executive-summary">
    <h2>Executive Summary</h2>
    <div class="summary-grid">
      <div class="summary-item">
        <div class="summary-value">${targetYield.toFixed(2)}%</div>
        <div class="summary-label">Annual Yield</div>
      </div>
      <div class="summary-item">
        <div class="summary-value">${formatCurrency(investorPrice)}</div>
        <div class="summary-label">Investment Required</div>
      </div>
      <div class="summary-item">
        <div class="summary-value">${termYears} yrs</div>
        <div class="summary-label">Investment Term</div>
      </div>
    </div>
  </div>

  <div class="card">
    <h3>Deal Overview: ${dealTitle}</h3>
    <p style="font-size: 16px; line-height: 1.6;">
      This is a secured investment in a performing land contract on a residential property 
      located at <strong>${propertyAddress}</strong>. The borrower has been making regular payments 
      and the property serves as collateral for your investment.
    </p>
    
    <div class="details-grid">
      <div class="details-section">
        <h4>Property Details</h4>
        <div class="detail-row">
          <span class="detail-label">Property Address:</span>
          <span class="detail-value">${propertyAddress}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Property Value:</span>
          <span class="detail-value">${formatCurrency(propertyValue)}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Investment-to-Value:</span>
          <span class="detail-value">${itv.toFixed(2)}%</span>
        </div>
      </div>
      
      <div class="details-section">
        <h4>Contract Terms</h4>
        <div class="detail-row">
          <span class="detail-label">Current Balance:</span>
          <span class="detail-value">${formatCurrency(currentBalance)}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Monthly Payment:</span>
          <span class="detail-value">${formatCurrency(monthlyPayment)}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Remaining Term:</span>
          <span class="detail-value">${paymentsRemaining} months</span>
        </div>
      </div>
    </div>
  </div>

  <div class="card">
    <h3>Cash Flow Analysis</h3>
    <div class="cash-flow-box">
      <h4 style="margin-top: 0; margin-bottom: 15px;">Your Investment Returns</h4>
      <div class="cash-flow-row" style="font-size: 18px;">
        <span>Initial Investment:</span>
        <span class="negative">-${formatCurrency(investorPrice)}</span>
      </div>
      <div class="divider"></div>
      <div class="cash-flow-row">
        <span>Monthly Payments (${paymentsRemaining} × ${formatCurrency(monthlyPayment)}):</span>
        <span class="positive">+${formatCurrency(totalMonthlyPayments)}</span>
      </div>
      <div class="cash-flow-row">
        <span>Balloon Payment (${balloonDueDate}):</span>
        <span class="positive">+${formatCurrency(balloonAmount)}</span>
      </div>
      <div class="divider-heavy"></div>
      <div class="cash-flow-row" style="font-size: 18px;">
        <span style="font-weight: bold;">Total Cash Received:</span>
        <span class="positive" style="font-size: 20px;">+${formatCurrency(totalReturn)}</span>
      </div>
      <div class="cash-flow-row highlight">
        <span>Your Total Profit:</span>
        <span>+${formatCurrency(totalProfit)}</span>
      </div>
    </div>

    <div class="metrics-grid">
      <div class="metric-card">
        <div class="metric-value">${roi}%</div>
        <div class="metric-label">Total ROI</div>
      </div>
      <div class="metric-card">
        <div class="metric-value">${targetYield.toFixed(2)}%</div>
        <div class="metric-label">Annual Yield</div>
      </div>
      <div class="metric-card">
        <div class="metric-value">${formatCurrency(monthlyPayment)}</div>
        <div class="metric-label">Monthly Income</div>
      </div>
    </div>
  </div>

  <div class="card">
    <h3>Investment Security</h3>
    <div class="security-item">
      <div class="security-number">1</div>
      <div class="security-content">
        <h5>Real Estate Collateral</h5>
        <p>Your investment is secured by real estate worth ${formatCurrency(propertyValue)}. 
        Your investment represents only ${itv.toFixed(2)}% of the property value, providing significant equity cushion.</p>
      </div>
    </div>
    <div class="security-item">
      <div class="security-number">2</div>
      <div class="security-content">
        <h5>Performing Contract</h5>
        <p>The borrower has an established payment history and is current on all payments.</p>
      </div>
    </div>
    <div class="security-item">
      <div class="security-number">3</div>
      <div class="security-content">
        <h5>Priority Position</h5>
        <p>You will hold a first-position lien on the property, giving you priority rights in case of default.</p>
      </div>
    </div>
  </div>

  <div class="summary-card">
    <h3>Why This is a Strong Investment</h3>
    <ul>
      <li><strong>Consistent Income:</strong> Receive ${formatCurrency(monthlyPayment)} every month for ${termYears} years</li>
      <li><strong>Strong Yield:</strong> ${targetYield.toFixed(2)}% annual return - significantly higher than traditional investments</li>
      <li><strong>Low Risk:</strong> Only ${itv.toFixed(2)}% loan-to-value ratio provides substantial equity protection</li>
      <li><strong>Secured Investment:</strong> Your investment is backed by real estate worth ${formatCurrency(propertyValue)}</li>
      <li><strong>Clear Exit:</strong> Balloon payment of ${formatCurrency(balloonAmount)} due on ${balloonDueDate}</li>
    </ul>
  </div>
</body>
</html>
    `.trim();
  };

  const handleCopyEmailHTML = async () => {
    try {
      const html = generateEmailHTML();
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([html], { type: 'text/plain' })
        })
      ]);
      toast.success("Email HTML copied! Paste into your email client to preserve formatting.");
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error("Failed to copy. Please try again.");
    }
  };

  // Calculate cash flow
  const totalMonthlyPayments = monthlyPayment * paymentsRemaining;
  const totalReturn = totalMonthlyPayments + balloonAmount;
  const totalProfit = totalReturn - investorPrice;
  const roi = ((totalProfit / investorPrice) * 100).toFixed(2);
  const termYears = (paymentsRemaining / 12).toFixed(1);

  return (
    <>
      {/* Simplified Print-Only Version */}
      <div className="hidden print:block" id="print-only-version">
        <div className="text-center mb-2">
          <h1 className="text-base font-bold mb-0">Investment Opportunity Review</h1>
          <h2 className="text-sm font-semibold">{dealTitle}</h2>
          <p className="text-xs">{propertyAddress}</p>
        </div>

        <div className="grid grid-cols-3 gap-1 mb-2 text-center border border-black p-1">
          <div>
            <div className="text-lg font-bold">{targetYield.toFixed(2)}%</div>
            <div className="text-xs">Yield</div>
          </div>
          <div>
            <div className="text-lg font-bold">{formatCurrency(investorPrice)}</div>
            <div className="text-xs">Investment</div>
          </div>
          <div>
            <div className="text-lg font-bold">{termYears} yrs</div>
            <div className="text-xs">Term</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
          <div>
            <div className="font-bold text-xs mb-1 border-b border-black">Property</div>
            <div className="flex justify-between"><span>Value:</span><span>{formatCurrency(propertyValue)}</span></div>
            <div className="flex justify-between"><span>ITV:</span><span>{itv.toFixed(2)}%</span></div>
          </div>
          <div>
            <div className="font-bold text-xs mb-1 border-b border-black">Contract</div>
            <div className="flex justify-between"><span>Balance:</span><span>{formatCurrency(currentBalance)}</span></div>
            <div className="flex justify-between"><span>Monthly:</span><span>{formatCurrency(monthlyPayment)}</span></div>
            <div className="flex justify-between"><span>Term:</span><span>{paymentsRemaining} mo</span></div>
          </div>
        </div>

        <div className="border border-black p-2 mb-2 text-xs">
          <div className="font-bold text-xs mb-1 text-center">Cash Flow</div>
          <div className="flex justify-between"><span>Investment:</span><span className="text-red-600">-{formatCurrency(investorPrice)}</span></div>
          <div className="flex justify-between"><span>Payments:</span><span className="text-green-600">+{formatCurrency(totalMonthlyPayments)}</span></div>
          <div className="flex justify-between"><span>Balloon:</span><span className="text-green-600">+{formatCurrency(balloonAmount)}</span></div>
          <div className="border-t border-black my-1"></div>
          <div className="flex justify-between font-bold"><span>Total Return:</span><span className="text-green-600">+{formatCurrency(totalReturn)}</span></div>
          <div className="flex justify-between font-bold text-sm"><span>Profit:</span><span>+{formatCurrency(totalProfit)}</span></div>
        </div>

        <div className="grid grid-cols-3 gap-1 mb-2 text-center text-xs">
          <div className="border border-black p-1">
            <div className="text-base font-bold">{roi}%</div>
            <div className="text-xs">ROI</div>
          </div>
          <div className="border border-black p-1">
            <div className="text-base font-bold">{targetYield.toFixed(2)}%</div>
            <div className="text-xs">Yield</div>
          </div>
          <div className="border border-black p-1">
            <div className="text-base font-bold">{formatCurrency(monthlyPayment)}</div>
            <div className="text-xs">Monthly</div>
          </div>
        </div>

        <div className="text-xs">
          <div className="font-bold text-xs mb-1">Security:</div>
          <ul className="ml-3 space-y-0 list-disc">
            <li>Real estate: {formatCurrency(propertyValue)} ({itv.toFixed(2)}% ITV)</li>
            <li>First-position lien with priority rights</li>
            <li>Performing contract, current payments</li>
          </ul>
        </div>
      </div>

      {/* Screen Version */}
      <div id="investor-review-print" className="space-y-6 print:hidden">
        <div className="flex justify-between items-center">
          <h2 className="text-3xl font-bold">Investment Opportunity Review</h2>
          <div className="flex gap-2">
            <Button onClick={handleCopyEmailHTML} variant="outline" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Copy for Email
            </Button>
            <Button onClick={handlePrint} className="flex items-center gap-2">
              <Printer className="h-4 w-4" />
              Print Review
            </Button>
          </div>
        </div>

      {/* Executive Summary */}
      <Card className="border-2 border-primary">
        <CardHeader className="bg-primary/5">
          <CardTitle className="text-2xl">Executive Summary</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary">{targetYield.toFixed(2)}%</div>
              <div className="text-sm text-muted-foreground mt-1">Annual Yield</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary">{formatCurrency(investorPrice)}</div>
              <div className="text-sm text-muted-foreground mt-1">Investment Required</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary">{termYears} yrs</div>
              <div className="text-sm text-muted-foreground mt-1">Investment Term</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deal Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Deal Overview: {dealTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="prose max-w-none">
              <p className="text-lg">
                This is a secured investment in a performing land contract on a residential property 
                located at <strong>{propertyAddress}</strong>. The borrower has been making regular payments 
                and the property serves as collateral for your investment.
              </p>
            </div>
            
            <div className="grid grid-cols-1 gap-6 mt-6">
              <div>
                <h4 className="font-semibold text-lg mb-3">Property Details</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Property Address:</span>
                    <span className="font-medium">{propertyAddress}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Property Value:</span>
                    <span className="font-medium">{formatCurrency(propertyValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Investment-to-Value:</span>
                    <span className="font-medium">{itv.toFixed(2)}%</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-lg mb-3">Contract Terms</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Current Balance:</span>
                    <span className="font-medium">{formatCurrency(currentBalance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Monthly Payment:</span>
                    <span className="font-medium">{formatCurrency(monthlyPayment)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Remaining Term:</span>
                    <span className="font-medium">{paymentsRemaining} months</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cash Flow Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Cash Flow Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="bg-muted/50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4">Your Investment Returns</h4>
              <div className="space-y-3">
                <div className="flex justify-between text-lg">
                  <span>Initial Investment:</span>
                  <span className="font-bold text-red-600">-{formatCurrency(investorPrice)}</span>
                </div>
                <div className="border-t border-border my-3" />
                <div className="flex justify-between">
                  <span>Monthly Payments ({paymentsRemaining} × {formatCurrency(monthlyPayment)}):</span>
                  <span className="font-semibold text-green-600">+{formatCurrency(totalMonthlyPayments)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Balloon Payment ({balloonDueDate}):</span>
                  <span className="font-semibold text-green-600">+{formatCurrency(balloonAmount)}</span>
                </div>
                <div className="border-t-2 border-border my-3" />
                <div className="flex justify-between text-xl font-bold">
                  <span>Total Cash Received:</span>
                  <span className="text-green-600">+{formatCurrency(totalReturn)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-primary">
                  <span>Your Total Profit:</span>
                  <span>+{formatCurrency(totalProfit)}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <div className="text-2xl font-bold text-primary">{roi}%</div>
                  <div className="text-sm text-muted-foreground mt-1">Total ROI</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <div className="text-2xl font-bold text-primary">{targetYield.toFixed(2)}%</div>
                  <div className="text-sm text-muted-foreground mt-1">Annual Yield</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <div className="text-2xl font-bold text-primary">{formatCurrency(monthlyPayment)}</div>
                  <div className="text-sm text-muted-foreground mt-1">Monthly Income</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Investment Security */}
      <Card>
        <CardHeader>
          <CardTitle>Investment Security</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-primary font-bold">1</span>
              </div>
              <div>
                <h5 className="font-semibold">Real Estate Collateral</h5>
                <p className="text-muted-foreground">
                  Your investment is secured by real estate worth {formatCurrency(propertyValue)}. 
                  Your investment represents only {itv.toFixed(2)}% of the property value, providing significant equity cushion.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-primary font-bold">2</span>
              </div>
              <div>
                <h5 className="font-semibold">Performing Contract</h5>
                <p className="text-muted-foreground">
                  The borrower has an established payment history and is current on all payments.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-primary font-bold">3</span>
              </div>
              <div>
                <h5 className="font-semibold">Priority Position</h5>
                <p className="text-muted-foreground">
                  You will hold a first-position lien on the property, giving you priority rights in case of default.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="border-2 border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="prose max-w-none">
            <h3 className="text-xl font-bold mb-3">Why This is a Strong Investment</h3>
            <ul className="space-y-2">
              <li>
                <strong>Consistent Income:</strong> Receive {formatCurrency(monthlyPayment)} every month for {termYears} years
              </li>
              <li>
                <strong>Strong Yield:</strong> {targetYield.toFixed(2)}% annual return - significantly higher than traditional investments
              </li>
              <li>
                <strong>Low Risk:</strong> Only {itv.toFixed(2)}% loan-to-value ratio provides substantial equity protection
              </li>
              <li>
                <strong>Secured Investment:</strong> Your investment is backed by real estate worth {formatCurrency(propertyValue)}
              </li>
              <li>
                <strong>Clear Exit:</strong> Balloon payment of {formatCurrency(balloonAmount)} due on {balloonDueDate}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <style>{`
        @media print {
          @page {
            margin: 0.75in;
            size: letter portrait;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          /* Prevent page breaks inside cards */
          .space-y-6 > * {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          /* Ensure no duplicates */
          body > :not([data-print-content]) {
            display: none;
          }
          [data-print-content] {
            display: block !important;
          }
        }
      `}</style>
      </div>
    </>
  );
};
